import { RenderedPdf, useRenderPdfFromDocuments } from './useRenderPdfFromDocuments.query'
import { useCallback } from 'react'
import { downloadBlob } from '../../../misc/blob.utilities'
import { isPrescription } from '../../../misc/documents.utilities'
import { DocumentInstance } from '../../../model/DocumentInstance'
import { DocumentCategoryKeys } from '@follow/cdk'
import { DocumentFormat } from '../../../model/Document'
import { CustomFile, RenderableFileExtension } from '../../../model/File'
import { isRenderableFileExtension } from '../../../misc/files.utilities'
import { useDirectDownloadFile } from '../files/useDirectDownloadFile.query'
import { useDownloadingContext } from '../../../core/hooks/useDownloadingContext'
import { useActionDispatch } from '../../utils'
import { addInfo, addWarning } from '../../../store/message'

type DownloadableDocument = Pick<DocumentInstance, 'id' | 'type'> & {
  category: DocumentCategoryKeys
}
type DownloadableFile = Pick<CustomFile, 'id' | 'extension'>

export type DownloadableDocumentsVariable = {
  documents?: DownloadableDocument[]
  files?: DownloadableFile[]
  fileName?: string
  format?: DocumentFormat
}

export const useDownloadDocuments = () => {
  const {
    isLoading,
    currentItems,
    error,
    startDownloading,
    finishDownloading,
    setError,
    resetError,
  } = useDownloadingContext()

  const dispatchInfo = useActionDispatch(addInfo)
  const dispatchError = useActionDispatch(addWarning)

  const download = useCallback(
    (renderedPdf: RenderedPdf) => {
      downloadBlob(renderedPdf.blob, renderedPdf.fileName || 'Document')
      finishDownloading()
    },
    [finishDownloading],
  )

  const { render } = useRenderPdfFromDocuments()
  const directDownload = useDirectDownloadFile()

  const downloadDocuments = useCallback(
    ({ documents = [], files = [], fileName, format }: DownloadableDocumentsVariable) => {
      if (documents.length === 0 && files.length === 0) {
        return
      }

      const itemsToDownload = [
        ...documents.map((d) => ({
          kind: 'document' as const,
          id: d.id,
        })),
        ...files.map((f) => ({
          kind: 'file' as const,
          id: f.id,
        })),
      ]
      startDownloading(itemsToDownload)
      setError(null)

      dispatchInfo(
        'Téléchargement en cours',
        `Merci de patienter, ${itemsToDownload?.length >= 2 ? 'vos documents sont en cours de préparation et seront prêts sous peu.' : 'votre document est en cours de préparation et sera prêt sous peu.'}`,
      )

      // Cas spécial du téléchargement d'un seul fichier -> pas d'appel au Renderer
      if (documents.length === 0 && files.length === 1) {
        directDownload({ fileId: files[0].id })
        finishDownloading()
        return
      }

      const documentsToLock = documents
        .filter(({ category, type }) => type === 'farte' && category && isPrescription(category))
        .map(({ id }) => id)

      render(
        {
          documents: documents.map((document) => ({
            documentId: document.id,
            type: document.type,
            documentCategoryId: document.category,
          })),
          files: files
            .filter((file) => isRenderableFileExtension(file.extension))
            .map((file) => ({
              fileId: file.id,
              extension: file.extension as RenderableFileExtension,
            })),
          documentsToLock,
          format,
          fileName,
        },
        {
          onSuccess: download,
          onError: (e: unknown) => {
            setError(e)
            dispatchError(
              'Téléchargement interrompu',
              'Une erreur est survenue lors de la génération du document. Veuillez réessayer ou contacter le support si le problème persiste.',
            )
            finishDownloading()
          },
        },
      )
    },
    [
      startDownloading,
      finishDownloading,
      setError,
      directDownload,
      render,
      download,
      dispatchError,
      dispatchInfo,
    ],
  )
  return { download: downloadDocuments, isLoading, error, resetError, currentItems }
}
